import Alpine, { AlpineComponent } from "alpinejs";
import { BasketService, GoogleService } from "../api";
import { MiniCartStore } from "./store/miniCartStore";

type BuyButton = AlpineComponent<{
	quantity: number;
	addToCart: () => void;
	increment: () => void;
	decrement: () => void;
	sanitize: () => void;
	checkStock: () => void;
	sleep: (ms: number) => Promise<void>;
	showQuantityNotification: boolean;
	disableBuyButton: boolean;
	isLoading: boolean;
	displaySuccess: boolean;
	hasMLEUnit: boolean;
	MLEFactor: number;
}>;

export default (
	partNo: string,
	numberInStock: number,
	articleStatus: number,
	articleStatusConstants: { StockOrder: number; ExpiringOrder: number; MadeToOrder: number },
	priceListId: number,
	hasMLEUnit: boolean,
	MLEFactor: number
): BuyButton => {
	return {
		quantity: hasMLEUnit ? MLEFactor : 1,
		showQuantityNotification: false,
		disableBuyButton: false,
		isLoading: false,
		displaySuccess: false,
		hasMLEUnit: hasMLEUnit,
		MLEFactor: MLEFactor,
		sleep(ms) {
			return new Promise((resolve) => setTimeout(resolve, ms));
		},
		async addToCart() {
			if (!this.hasMLEUnit) {
				this.sanitize();
			}

			this.checkStock();

			if (!this.disableBuyButton) {
				this.isLoading = true;
				try {
					const response = await BasketService.addToBasket({
						body: { partNo: partNo, quantity: this.quantity, priceListId: priceListId },
					});

					await this.sleep(500);
					this.isLoading = false;

					let numberOfItems = 0;
					response.data?.items.forEach((item) => {
						if (item.hasMLEUnit) {
							numberOfItems += item.quantity / item.mleFactor;
						} else {
							numberOfItems += item.quantity;
						}
					});

					(Alpine.store("cart") as MiniCartStore).setItemCount(numberOfItems, true);
					const googleTracking = await GoogleService.addToCart({
						body: { partNo: partNo, quantity: this.quantity, priceListId: priceListId },
					});
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(window as any).dataLayer.push({ ecommerce: null });
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(window as any).dataLayer.push({
						event: "add_to_cart",
						ecommerce: googleTracking.data,
					});
					this.displaySuccess = true;
					await this.sleep(2000);
					this.displaySuccess = false;
				} catch (error) {
					console.error("Error adding to basket:", error);
				} finally {
					this.isLoading = false;
				}
			}
		},
		increment() {
			if (this.quantity < 9999) {
				if (this.hasMLEUnit) {
					this.quantity = Math.round((this.quantity + this.MLEFactor) * 100) / 100;
				} else {
					this.quantity += 1;
				}
			}
		},
		decrement() {
			if (this.hasMLEUnit) {
				if (this.quantity > this.MLEFactor) {
					this.quantity = Math.round((this.quantity - this.MLEFactor) * 100) / 100;
				}
			} else {
				if (this.quantity > 1) {
					this.quantity -= 1;
				}
			}
		},
		sanitize() {
			if (this.quantity > 9999) {
				this.quantity = 9999;
			} else if (this.quantity < 1 || isNaN(this.quantity)) {
				this.quantity = 1;
			} else {
				this.quantity = Math.floor(this.quantity);
			}
		},
		checkStock() {
			const exceedsStock = this.quantity > numberInStock;

			this.showQuantityNotification = false;
			this.disableBuyButton = false;

			if (exceedsStock) {
				switch (articleStatus) {
					case articleStatusConstants.StockOrder:
						if (numberInStock > 0) {
							this.showQuantityNotification = true;
						}
						break;

					case articleStatusConstants.ExpiringOrder:
						this.showQuantityNotification = true;
						this.disableBuyButton = true;
						break;

					case articleStatusConstants.MadeToOrder:
						if (numberInStock > 0) {
							this.showQuantityNotification = true;
						}
						break;
				}
			}
		},
	};
};
